import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import cn from "classnames";

// Mui components
import { Alert, Box, IconButton, FormControl, LinearProgress, TextField, Typography } from "@mui/material";

// Components
import HelperText from "components/HelperText";
import { FieldHintButton } from "components/FieldHintButton";
import AutocompleteField, {
    useAutocompleteCompanies,
    useAutocompleteDrxWorkPosition,
} from "components/Autocomplete/AutocompleteField";

// Types
import { DrxWorkPositionName } from "services/reference/reference.types";

// Services
import { CompanyNameDomains } from "services/company";

// Styles
import profileStyles from "./LetsGetStarted.styles";
import CustomIcon from "components/CustomIcon";
import icons from "enums/icons";

const LetsGetStarted = ({
    selectedWorkPosition,
    setSelectedWorkPosition,
    selectedCompany,
    setSelectedCompany,
    selectedCompanyWebsite,
    setSelectedCompanyWebsite,
    selectedLinkedinId,
    setSelectedLinkedinId,
    errors,
    setErrors,
}: {
    selectedWorkPosition: DrxWorkPositionName | string | null;
    setSelectedWorkPosition: React.Dispatch<React.SetStateAction<DrxWorkPositionName | string | null>>;
    selectedCompany: CompanyNameDomains | string | null;
    setSelectedCompany: React.Dispatch<React.SetStateAction<CompanyNameDomains | string | null>>;
    selectedCompanyWebsite: string;
    setSelectedCompanyWebsite: React.Dispatch<React.SetStateAction<string>>;
    selectedLinkedinId: string;
    setSelectedLinkedinId: React.Dispatch<React.SetStateAction<string>>;
    errors: { company: boolean; workPosition: boolean; linkedIn: boolean };
    setErrors: React.Dispatch<React.SetStateAction<{ company: boolean; workPosition: boolean; linkedIn: boolean }>>;
}) => {
    const classes = profileStyles();
    const intl = useIntl();
    const [showAlert, setShowAlert] = useState(false);
    const [showWarning, setShowWarning] = useState(false);

    // Company
    const {
        companiesList,
        placeholderCompany,
        setCompaniesList,
        getOptionLabelCompany,
        renderOptionCompany,
        handleInputChangeCompany,
    } = useAutocompleteCompanies();

    const onInputChangeCompany = async (needle: string) => {
        if (errors.company) {
            setErrors((errors) => ({ ...errors, company: false }));
        }

        setSelectedCompany(needle);
        await handleInputChangeCompany(needle);

        if (!needle.length) {
            setSelectedCompanyWebsite("");
        }
    };

    const onChangeCompany = (value: Pick<CompanyNameDomains, "id" | "name"> | string | null) => {
        if (errors.company) {
            setErrors((errors) => ({ ...errors, company: false }));
        }
        setSelectedCompany(value as CompanyNameDomains);
    };

    // Roles
    const {
        drxWorkPositionsList,
        placeholderDrxWorkPosition,
        setDrxWorkPositionsList,
        getOptionLabelDrxWorkPosition,
        renderOptionDrxWorkPosition,
        handleInputChangeDrxWorkPosition,
    } = useAutocompleteDrxWorkPosition();

    const onInputChangeWorkPosition = async (needle: string) => {
        if (errors.workPosition) {
            setErrors((errors) => ({ ...errors, workPosition: false }));
        }
        setSelectedWorkPosition(needle);
        await handleInputChangeDrxWorkPosition(needle);
    };

    const onChangeWorkPosition = (value: Pick<DrxWorkPositionName, "id" | "name"> | string | null) => {
        if (errors.workPosition) {
            setErrors((errors) => ({ ...errors, workPosition: false }));
        }
        setSelectedWorkPosition(value as DrxWorkPositionName);
    };

    // Company Website
    const shouldDisableWebsiteField = Boolean(
        typeof selectedCompany !== "string" || !(selectedCompany as string).trim().length
    );

    const handleCompanyWebsiteOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCompanyWebsite(event.target.value);
    };

    // Linkedin Id
    const handleLinkedinIdOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (errors.linkedIn) {
            setErrors((errors) => ({ ...errors, linkedIn: false }));
        }
        setSelectedLinkedinId(event.target.value.trim());
    };

    useEffect(() => {
        if (errors.company || errors.workPosition) {
            setShowAlert(true);
        }
        if (errors.linkedIn) {
            setShowWarning(true);
        }
    }, [errors]);

    return (
        <>
            <Box className={classes.root}>
                <Typography className={classes.title}>
                    {intl.formatMessage({ id: "wizard.letsGetStarted.title" })}
                </Typography>
                <LinearProgress className={classes.progressBar} variant="determinate" value={50} />
                <Typography className={classes.info}>
                    {intl.formatMessage({ id: "wizard.letsGetStarted.titleExplainer1" })}
                </Typography>
                <Typography className={classes.info}>
                    {intl.formatMessage({ id: "wizard.letsGetStarted.titleExplainer2" })}
                </Typography>
                {showAlert && (errors.company || errors.workPosition) && (
                    <Alert
                        iconMapping={{ warning: <CustomIcon icon={icons.errorTriangle} /> }}
                        variant="filled"
                        severity="warning"
                        sx={{ margin: "auto", mb: 2, position: "relative" }}
                        action={
                            <IconButton size="small" onClick={() => setShowAlert(false)} sx={{ alignSelf: "center" }}>
                                <CustomIcon icon={icons.close} sx={{ color: "common.white", fontSize: 12 }} />
                            </IconButton>
                        }
                    >
                        <FormattedMessage id="wizard.letsGetStarted.error" />
                    </Alert>
                )}
                {showWarning && !errors.company && !errors.workPosition && errors.linkedIn && (
                    <Alert
                        iconMapping={{ warning: <CustomIcon icon={icons.eye} /> }}
                        variant="filled"
                        severity="info"
                        sx={{ margin: "auto", mb: 2, position: "relative" }}
                        action={
                            <IconButton size="small" onClick={() => setShowWarning(false)} sx={{ alignSelf: "center" }}>
                                <CustomIcon icon={icons.close} sx={{ color: "common.white", fontSize: 12 }} />
                            </IconButton>
                        }
                    >
                        <FormattedMessage id="wizard.letsGetStarted.linkedIn.error" />
                    </Alert>
                )}
                <Box className={classes.formFieldsWrapper}>
                    <Box className={cn(classes.formFields, classes.formFieldsSmMb)}>
                        <AutocompleteField
                            id="wizard-your-role"
                            blurOnSelect
                            onInputChangeCallback={onInputChangeWorkPosition}
                            onOptionSelectCallback={onChangeWorkPosition}
                            showEndAdorment={false}
                            freeSolo={true}
                            showValue={true}
                            disableClearable={false}
                            inputValue={selectedWorkPosition}
                            inputSize="small"
                            options={drxWorkPositionsList}
                            optionsUpdater={setDrxWorkPositionsList}
                            openOnlyOnOptionsLoaded={true}
                            getOptionLabel={getOptionLabelDrxWorkPosition}
                            renderOption={renderOptionDrxWorkPosition}
                            label={placeholderDrxWorkPosition}
                            helperText={<HelperText sx={errors.workPosition ? { color: "red" } : null} />}
                            filterOptions={(x) => x}
                            error={errors.workPosition}
                        />
                    </Box>

                    <Box className={cn(classes.formFields, classes.formFieldsSmMb)}>
                        <AutocompleteField
                            id="wizard-your-company"
                            blurOnSelect
                            onInputChangeCallback={onInputChangeCompany}
                            onOptionSelectCallback={onChangeCompany}
                            showEndAdorment={false}
                            freeSolo={true}
                            showValue={true}
                            disableClearable={false}
                            inputValue={selectedCompany}
                            inputSize="small"
                            options={companiesList}
                            optionsUpdater={setCompaniesList}
                            openOnlyOnOptionsLoaded={true}
                            getOptionLabel={getOptionLabelCompany}
                            renderOption={renderOptionCompany}
                            label={placeholderCompany}
                            helperText={<HelperText sx={errors.company ? { color: "red" } : null} />}
                            filterOptions={(x: CompanyNameDomains) => x}
                            error={errors.company}
                            hintButton={
                                <FieldHintButton>
                                    <FormattedMessage id="field.hint.company.search" />
                                </FieldHintButton>
                            }
                        />
                    </Box>

                    <FormControl className={classes.formFields}>
                        <TextField
                            id="company-website"
                            size="small"
                            value={selectedCompanyWebsite}
                            onChange={handleCompanyWebsiteOnChange}
                            label={intl.formatMessage({ id: "wizard.letsGetStarted.companyWebsite" })}
                            disabled={shouldDisableWebsiteField}
                            helperText={
                                <HelperText
                                    message={"*Only required if company not found in ‘Add current company’ search"}
                                />
                            }
                        />
                    </FormControl>

                    <FormControl className={classes.formFields}>
                        <TextField
                            id="your-linkedinId"
                            size="small"
                            value={selectedLinkedinId}
                            onChange={handleLinkedinIdOnChange}
                            label={intl.formatMessage({ id: "wizard.letsGetStarted.linkedinId" })}
                            helperText={
                                errors.linkedIn ? (
                                    <HelperText
                                        message="Example of valid LinkedIn URL: https://linkedin.com/in/joe-bloggs-123456/?originalSubdomain=uk | Example of valid LinkedIn ID: joe-bloggs-123456"
                                        sx={errors.linkedIn ? { color: "info.main" } : null}
                                    />
                                ) : (
                                    <></>
                                )
                            }
                            InputProps={{
                                endAdornment: (
                                    <FieldHintButton popoverMaxWidth={295}>
                                        <FormattedMessage id="wizard.letsGetStarted.tooltipText" />
                                    </FieldHintButton>
                                ),
                            }}
                        />
                    </FormControl>
                </Box>
            </Box>
        </>
    );
};

export default LetsGetStarted;
